import { i18n } from 'lib/i18n.js';
import * as queries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import { mapActions } from 'vuex';
import { convertGQLToProductJson, formatPrice, trapFocus } from 'utils/general';
import { isTablet } from 'utils/general';
import on, { off } from 'dom-event';
import debounce from 'lodash.debounce';
import {
  listProductsEventData,
  trackEvent,
} from '../../assets/js/lib/tracking';
import VimeoHelper from "lib/vimeoHelper";
import {Pagination, A11y, Keyboard} from "swiper/modules";

export default {
  props: ['look', 'componentName', 'siteSection'],
  i18n,
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'vue-swiper': () => import('../vue-swiper/vue-swiper.vue'),
    'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
  },
  data() {
    return {
      productsData: [],
      isTablet: isTablet(),
      isDraging: false,
      isThumbClick: false,
      swiperOptions: {
        slidesPerView: 1,
        a11y: true,
        keyboard: true,
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
        autoHeight: true,
        on: {
          slideChange: (swiper) => {
            if(!this.isThumbClick) {
              trackEvent({
                event: 'slider_interactions',
                click_action: this.isDraging ? 'swipe' : 'select',
                section_name: this.look.name,
                component_name: document.body.dataset.origin + '/' + this.componentName,
                image_index: `${swiper.realIndex + 1}`,
                ...(this.siteSection && { site_section: this.siteSection }),
              });
            } else
              this.isThumbClick = false;
          }
        },
        modules: [Pagination, A11y, Keyboard]
      },
      addingToCart: false,
      video: null,
      isPaused: false,
      isMuted: true,
      video_link: null,
      hasAudio: false
    };
  },
  mounted() {
    this.fetchProductsData();
    on(window, 'resize', debounce(this.onResize, 300));
  },
  updated() {
    const element = this.$el.parentElement;
    if (element.classList.contains('active')) {
      trapFocus(element);
    }
  },
  beforeDestroy() {
    off(window, 'resize', debounce(this.onResize, 300));
  },
  computed: {
    btnText() {
      return this.addingToCart
        ? this.$t('products.card.adding')
        : this.$t('products.product.add_all_products');
    },
    iFrameSrc() {
      if (!this.look.video_link) return false;

      const str = this.look.video_link.replace(
        'vimeo.com/',
        'player.vimeo.com/video/',
      );

      this.video_link = `${str}?background=1`;
      return `${str}?background=1`;
    },
  },
  watch: {
    video_link() {
      setTimeout(() => {
        this.video = new Vimeo.Player(this.$refs.iFrameEl);
      }, 100);
    },
  },
  methods: {
    ...mapActions('cart', ['addMultipleItemsToCart', 'addToCart', 'closeCart']),
    onResize() {
      this.isTablet = isTablet();
      const { looksModalCarousel } = this.$refs;
      if (looksModalCarousel) {
        console.log(looksModalCarousel.$swiper)
        looksModalCarousel.$swiper.update();
      }
    },
    closeModal() {
      this.$emit('close-modal');
    },
    async fetchProductsData() {
      if (!this.look.products) return;
      const ids = this.look.products?.map((product) => product.id);

      let storefrontResponse = null;
      const products = [];

      await Promise.all(
        ids.map(async (id, i) => {
          storefrontResponse = await storefront.request(
            queries.productLookDataByVariantId,
            {
              id: 'gid://shopify/ProductVariant/' + id,
            },
          );

          const p = {
            ...storefrontResponse.node.product,
            variants: {
              edges: [
                {
                  node: { ...storefrontResponse.node },
                },
              ],
            },
          };

          const convertedProduct = convertGQLToProductJson(p);
          const convertedVariants = convertedProduct.variants.map((variant) => {
            return {
              ...variant,
              formattedPrice: formatPrice(variant.price),
              formattedCompareAtPrice: formatPrice(variant.compareAtPrice),
              id: id,
            };
          });

          products[i] = {
            ...convertedProduct,
            index: i,
            variants: [...convertedVariants],
          };
        }),
      );

      this.productsData = products;

      const extraData = {
        listId: 'shoppable-look-book-list',
        listName: this.look.name,
        origin: document.body.dataset.origin,
        originProduct: document.body.dataset.originProduct,
      };

      const { products: eventProducts, hasBundleBuilder } = await listProductsEventData(
        this.productsData,
        extraData,
      );

      // track visible elements on page load
      let eventData = {
        event: 'view_item_list',
        ...(this.siteSection && { site_section: this.siteSection }),
        component_name: document.body.dataset.origin + '/' +  this.componentName,
        ecommerce: {
          item_list_id: 'shoppable-look-book-list',
          item_list_name: this.look.name,
          items: eventProducts,
        },
      }
      if (hasBundleBuilder) {
        eventData = {
          ...eventData,
          bundle_type: 'bundle builder'
        }
      }
      trackEvent(eventData);
      
      try {
        if (this.look.video_link) {
          const videoId = this.look.video_link.replace('https://vimeo.com/', '');
          const videoMetadata = await VimeoHelper.getVideoMetadata(videoId);
          if (videoMetadata) {
            this.hasAudio = Boolean(videoMetadata.has_audio);
          } else {
            console.error('Error getting video metadata');
            this.hasAudio = false;
          }
        }
      } catch (error) {
        console.error('Error checking audio for video: ', error);
      }
    },
    addProducts() {
      const data = this.productsData.filter(
        (product) => product.variants[0].available,
      );

      this.addMultipleItemsToCart({
        lines: data.reduce(
          ({ lines, index }, product) => {
            lines.push({
              id: product.variants[0].id,
              quantity: 1,
              product: product,
              properties: {
                _product_collection: product.categories
                  ? product.categories[0]
                  : null,
                _product_tags: product.tags,
                _product_rating: product.metafields?.yotpo.reviewsAverage,
                _promo_text: product.metafields?.cart_promo?.promo_text || '',
                _promo_link: product.metafields?.cart_promo?.link || '',
                _origin: document.body.dataset.origin,
                _origin_product: document.body.dataset.originProduct,
                _item_list_id: 'shoppable-lookbook',
                _item_list_name: 'Shoppable Lookbook',
                _index: index,
              },
            });
            index++;
            return { lines, index };
          },
          { lines: [], index: 0 },
        ).lines,
        quantity: 1,
        callback: () => this.closeCart(),
        otherData: [],
      });

      this.addingToCart = true;
      setTimeout(() => {
        this.addingToCart = false;
        this.closeModal();
      }, this.productsData.length * 500);
    },
    handlePauseClick() {
      this.isPaused ? this.video.play() : this.video.pause();
      this.isPaused = !this.isPaused;

      Promise.all([this.video.getDuration(), this.video.getVideoTitle()]).then(
        (values) => {
          trackEvent({
            event: 'video_engagement',
            video_name: values[1],
            video_duration: values[0],
            component_name: document.body.dataset.origin + '/' +  this.componentName,
            engagement_type: this.isPaused ? 'pause' : 'play',
            ...(this.siteSection && { site_section: this.siteSection }),
          });
        },
      );
    },
    handleSoundClick() {
      this.isMuted = !this.isMuted;
      this.video.setMuted(this.isMuted);
    },
    goToVideo() {
      this.isThumbClick = true;
      const { looksModalCarousel } = this.$refs;
      looksModalCarousel.$swiper.slideTo(1);

      Promise.all([this.video.getDuration(), this.video.getVideoTitle()]).then(
        (values) => {
          trackEvent({
            event: 'video_engagement',
            video_name: values[1],
            video_duration: values[0],
            component_name: document.body.dataset.origin + '/' + this.componentName,
            engagement_type: this.isPaused ? 'pause' : 'play',
            ...(this.siteSection && { site_section: this.siteSection }),
          });
        },
      );
    },
    onClickProduct(handle) {
      window.location.href = `${window.Shopify.routes.root}products/${handle}`;
    },
  },
};
